import { useMutation, useQuery } from '@apollo/client';
import { ArrowLeftStartOnRectangleIcon } from '@heroicons/react/24/outline';
import { ContentCopy } from '@mui/icons-material';
import { gql } from '__generated__/gql';
import dayjs from 'dayjs';
import { FC } from 'react';
import { UAParser } from 'ua-parser-js';

import DetailsPanel from 'components/DetailsPanel';
import ErrorMessage from 'components/ErrorMessage';

import Badge from 'primitives/Badge';
import Button from 'primitives/Button';
import LoadingIndicator from 'primitives/LoadingIndicator';
import Toast from 'primitives/Toast';
import useToast from 'primitives/Toast/useToast';

import { logout } from 'utils/auth';

import UpdateAvatar from './UpdateAvatar';

const LOGOUT_MUTATION = gql(`
  mutation LogoutUser {
    logoutUser
  }
`);

const LOGOUT_SESSION_MUTATION = gql(`
  mutation LogoutSession($sessionId: String!) {
    logoutSession(sessionId: $sessionId)
  }
`);

const USER_QUERY = gql(`
  query getUserGeneralSettings {
    dashboard {
      id
      user {
        id
        name
        phoneNumber
        email
        image {
          url
          name
        }
        loginHistory {
          id
          loginAt
          ipAddress
          userAgent
          sessionId
          isActive
          isCurrent
        }
      }
    }
  }
`);

function SessionStatusSection({
  isCurrent,
  isActive,
  sessionId,
  refetch,
}: {
  isCurrent?: boolean | null;
  isActive?: boolean | null;
  sessionId?: string | null;
  refetch: () => void;
}) {
  const [logoutSession, { loading, error }] = useMutation(LOGOUT_SESSION_MUTATION);

  if (error) {
    return <ErrorMessage error={error} refetch={refetch} />;
  }

  if (loading) {
    return <LoadingIndicator />;
  }

  if (isCurrent === true) {
    return (
      <div className="flex w-full justify-end">
        <Badge label="Current session" color="green" showIndicator size="sm" />
      </div>
    );
  }
  if (isActive === true) {
    return (
      <div className="flex w-full justify-end lg:w-[11rem]">
        <button
          type="button"
          className="appearance-none bg-transparent p-0 text-sm font-semibold text-indigo-600 hover:text-indigo-500"
          onClick={() => {
            logoutSession({ variables: { sessionId: sessionId || '' } }).then(() => {
              refetch();
            });
          }}
          aria-label="Log out this session"
        >
          Log out
        </button>
      </div>
    );
  }
  return <p className="w-full text-sm text-gray-500 lg:w-[11rem] lg:text-right">Signed out</p>;
}

function formatBrowserLabel(agent: ReturnType<UAParser['getBrowser']>): string {
  const name = agent.name?.trim();
  const version = agent.version?.trim();
  if (name && version) return `${name} ${version}`;
  if (name) return name;
  return 'Unknown';
}

function formatDeviceLabel(agent: ReturnType<UAParser['getDevice']>): string {
  const parts = [
    agent.type ? agent.type.charAt(0).toUpperCase() + agent.type.slice(1) : null,
    agent.vendor?.trim() || null,
    agent.model?.trim() || null,
  ].filter(Boolean) as string[];
  if (parts.length) return parts.join(' · ');
  return 'Desktop or unrecognized';
}

function formatOsLabel(agent: ReturnType<UAParser['getOS']>): string {
  const name = agent.name?.trim();
  const version = agent.version?.trim();
  if (name && version) return `${name} ${version}`;
  if (name) return name;
  return 'Unknown';
}

function parsedUserAgent(userAgent: string): {
  browser: ReturnType<UAParser['getBrowser']>;
  device: ReturnType<UAParser['getDevice']>;
  os: ReturnType<UAParser['getOS']>;
} {
  const parser = new UAParser(userAgent);
  return {
    browser: parser.getBrowser(),
    device: parser.getDevice(),
    os: parser.getOS(),
  };
}

const GeneralSettings: FC = () => {
  const { loading, error, data, refetch } = useQuery(USER_QUERY);
  const [logoutUser, { loading: logoutLoading, error: logoutError }] = useMutation(LOGOUT_MUTATION);
  const { showToast, handleToastOpen, handleToastClose } = useToast();

  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const user = data.dashboard.user;
  const loginHistory = data.dashboard.user.loginHistory;

  return (
    <>
      <DetailsPanel
        title="Profile"
        data={[
          {
            label: 'Name',
            value: user.name,
          },
          {
            label: 'Phone Number',
            value: user.phoneNumber,
          },
          {
            label: 'Email',
            value: user.email,
          },
        ]}
      />
      <UpdateAvatar id={user.id} avatar={user.image?.url} />

      <div className="shadow-sm ring-1 ring-gray-200 rounded-md sm:rounded-lg md:col-span-2">
        <div className="border-b border-gray-200 px-4 py-4 sm:px-4">
          <h2 className="text-lg font-semibold leading-7 text-gray-900 sm:truncate sm:text-lg sm:tracking-tight">
            Recent sign-ins
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-gray-600">
            Browsers and devices used to login to this account.
          </p>
        </div>
        {loginHistory && loginHistory.length > 0 ? (
          <ul className="divide-y divide-gray-100">
            {loginHistory.map(lh => {
              if (!lh?.id) return null;
              const ua = parsedUserAgent(lh.userAgent || '');

              return (
                <li key={lh.id} className="px-4 py-4 sm:px-4">
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,14rem)_repeat(3,minmax(0,1fr))_11rem] lg:gap-x-6 lg:items-start">
                    <div className="min-w-0 lg:max-w-[14rem]">
                      <p className="text-sm font-medium leading-6 text-gray-900">
                        {lh.loginAt ? dayjs(lh.loginAt).format('h:mm A, DD MMMM YYYY') : '—'}
                      </p>
                      <p className="mt-1 font-mono text-sm tabular-nums text-gray-500 flex items-center gap-1">
                        <ContentCopy
                          fontSize="inherit"
                          className="cursor-pointer hover:text-indigo-600"
                          onClick={e => {
                            e.stopPropagation();
                            handleToastOpen();
                            navigator.clipboard.writeText(lh.ipAddress || '');
                          }}
                        />
                        IP <span className="truncate">{lh.ipAddress?.trim() || '—'}</span>
                      </p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium uppercase tracking-wide text-gray-500">
                        Browser
                      </p>
                      <p className="mt-1 text-sm leading-6 text-gray-900">
                        {formatBrowserLabel(ua.browser)}
                      </p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium uppercase tracking-wide text-gray-500">
                        Device
                      </p>
                      <p className="mt-1 text-sm leading-6 text-gray-900">
                        {formatDeviceLabel(ua.device)}
                      </p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium uppercase tracking-wide text-gray-500">
                        Operating system
                      </p>
                      <p className="mt-1 text-sm leading-6 text-gray-900">{formatOsLabel(ua.os)}</p>
                    </div>
                    <div className="flex w-full min-w-0 flex-col items-stretch lg:w-[11rem] lg:shrink-0 lg:items-end">
                      <SessionStatusSection
                        isCurrent={lh.isCurrent}
                        isActive={lh.isActive}
                        sessionId={lh.sessionId}
                        refetch={refetch}
                      />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="px-4 py-10 text-center text-sm text-gray-500">
            No sign-ins recorded yet.
          </div>
        )}
      </div>

      <div className="mb-6 flex w-full justify-start items-start">
        <Button
          leadingIcon={<ArrowLeftStartOnRectangleIcon className="h-5 w-5" />}
          className="w-48 py-3 text-lg self-start"
          onClick={() => {
            logoutUser().then(() => {
              logout();
            });
          }}
          loading={logoutLoading}
          disabled={logoutLoading}
          disabledMessage={logoutError ? logoutError.message : undefined}
        >
          Logout
        </Button>
      </div>

      <Toast
        title="IP Copied"
        message={'The IP address has been copied to your clipboard.'}
        show={showToast}
        handleClose={handleToastClose}
      />
    </>
  );
};

export default GeneralSettings;
